import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Search, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, 
  Info, BarChart3, Clock, Loader2, Apple, Activity
} from 'lucide-react';
import { toast } from 'sonner';

export const CorrelationAnalyzer = ({ embedded = false }) => {
  const { API, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState(null);
  const [lagHours, setLagHours] = useState(24);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/correlation/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.get(`${API}/correlation/food-symptoms`, {
        params: { days: 60, lag_hours: lagHours },
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data);
      if (response.data.status === 'insufficient_data') {
        toast.info('Not enough data yet for reliable analysis');
      } else {
        toast.success('Analysis complete!');
      }
    } catch (error) {
      toast.error('Failed to run analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const getRiskColor = (score) => {
    if (score >= 7) return 'text-red-600 bg-red-50 border-red-200';
    if (score >= 4) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-stone-600 bg-stone-50 border-stone-200';
  };

  const getSafeColor = (score) => {
    if (score >= 7) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 4) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    return 'text-stone-600 bg-stone-50 border-stone-200';
  };

  return (
    <div className="space-y-6" data-testid="correlation-analyzer">
      {/* Header - only show if not embedded */}
      {!embedded && (
        <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8">
          <div className="flex items-center gap-4 mb-4">
            <Search className="w-10 h-10 text-primary" strokeWidth={1.5} />
            <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
              Food-Symptom Correlation Analyzer
            </h1>
          </div>
          <p className="text-[#57534E] leading-relaxed">
          Analyze your food logs and symptom entries to identify potential trigger foods and foods that correlate with better days.
        </p>
      </div>
      )}

      {embedded && (
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-6 h-6 text-primary" />
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">Food Correlations</h2>
        </div>
      )}

      {/* Disclaimer */}
      <Card className="bg-amber-50 border-amber-200 p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Important:</strong> This analysis shows correlations, not causation. Many factors affect symptoms. 
            Always consult healthcare providers before making dietary changes. Minimum 14 days of food logs and 
            7 days of symptom data required for analysis.
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-[#A8A29E]">Loading data summary...</p>
        </div>
      ) : (
        <>
          {/* Data Summary */}
          <Card className="bg-white rounded-xl border border-stone-100 p-6">
            <h2 className="font-nunito font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Your Data Summary
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <Apple className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#2D2D2D]">{summary?.data_summary?.food_days || 0}</p>
                <p className="text-sm text-[#57534E]">Days of food logs</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <Activity className="w-6 h-6 text-secondary-foreground mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#2D2D2D]">{summary?.data_summary?.symptom_days || 0}</p>
                <p className="text-sm text-[#57534E]">Days of symptom logs</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <p className="text-2xl font-bold text-[#2D2D2D]">{summary?.data_summary?.food_entries || 0}</p>
                <p className="text-sm text-[#57534E]">Food entries</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <p className="text-2xl font-bold text-[#2D2D2D]">{summary?.data_summary?.mood_checkins || 0}</p>
                <p className="text-sm text-[#57534E]">Mood check-ins</p>
              </div>
            </div>

            {/* Progress to analysis readiness */}
            {!summary?.ready_for_analysis && (
              <div className="bg-primary/10 rounded-lg p-4">
                <h3 className="font-semibold text-primary mb-2">Keep logging to unlock analysis!</h3>
                <div className="space-y-2">
                  {summary?.requirements?.food_days_needed > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${((14 - summary.requirements.food_days_needed) / 14) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-[#57534E]">
                        {summary.requirements.food_days_needed} more food log days needed
                      </span>
                    </div>
                  )}
                  {summary?.requirements?.symptom_days_needed > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-secondary rounded-full transition-all"
                          style={{ width: `${((7 - summary.requirements.symptom_days_needed) / 7) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-[#57534E]">
                        {summary.requirements.symptom_days_needed} more symptom log days needed
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Analysis controls */}
            {summary?.ready_for_analysis && (
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#57534E]" />
                  <label className="text-sm text-[#57534E]">Symptom lag time:</label>
                  <select
                    value={lagHours}
                    onChange={(e) => setLagHours(Number(e.target.value))}
                    className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm"
                  >
                    <option value={12}>12 hours</option>
                    <option value={24}>24 hours (1 day)</option>
                    <option value={48}>48 hours (2 days)</option>
                    <option value={72}>72 hours (3 days)</option>
                  </select>
                </div>
                <Button
                  onClick={runAnalysis}
                  disabled={analyzing}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
                  data-testid="run-analysis-btn"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Run Analysis
                    </>
                  )}
                </Button>
              </div>
            )}
          </Card>

          {/* Results */}
          {results && results.status === 'complete' && (
            <div className="space-y-6">
              {/* Overall Stats */}
              <Card className="bg-white rounded-xl border border-stone-100 p-6">
                <h2 className="font-nunito font-bold text-[#2D2D2D] mb-4">Analysis Overview</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#2D2D2D]">{results.overall_stats.unique_foods_tracked}</p>
                    <p className="text-sm text-[#57534E]">Unique foods tracked</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#2D2D2D]">{results.overall_stats.average_severity}/10</p>
                    <p className="text-sm text-[#57534E]">Avg symptom severity</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#2D2D2D]">{results.analysis_period.food_log_days}</p>
                    <p className="text-sm text-[#57534E]">Days analyzed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#2D2D2D]">{results.analysis_period.lag_hours_used}h</p>
                    <p className="text-sm text-[#57534E]">Lag time used</p>
                  </div>
                </div>
              </Card>

              {/* Potential Triggers */}
              <Card className="bg-white rounded-xl border border-stone-100 p-6">
                <h2 className="font-nunito font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Potential Trigger Foods
                </h2>
                {results.potential_triggers.length === 0 ? (
                  <p className="text-[#A8A29E] text-center py-6">
                    No clear trigger patterns identified. This is good news!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {results.potential_triggers.map((food, index) => (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg border ${getRiskColor(food.risk_score)}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <TrendingUp className="w-4 h-4" />
                              <span className="font-semibold">{food.food}</span>
                              <span className="text-xs bg-white px-2 py-0.5 rounded-full">
                                Risk: {food.risk_score}/10
                              </span>
                            </div>
                            <p className="text-sm mb-2">
                              {food.bad_day_ratio}% of days after eating this had elevated symptoms
                            </p>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span>Avg severity: {food.avg_severity_after}/10</span>
                              <span>•</span>
                              <span>Logged {food.occurrences} times</span>
                              <span>•</span>
                              <span>Confidence: {food.confidence}%</span>
                            </div>
                            {food.associated_symptoms.length > 0 && (
                              <div className="mt-2 text-xs">
                                <span className="font-medium">Associated symptoms: </span>
                                {food.associated_symptoms.map(s => s.symptom).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Safe Foods */}
              <Card className="bg-white rounded-xl border border-stone-100 p-6">
                <h2 className="font-nunito font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Foods Associated With Better Days
                </h2>
                {results.safe_foods.length === 0 ? (
                  <p className="text-[#A8A29E] text-center py-6">
                    Not enough data to identify safe food patterns yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {results.safe_foods.map((food, index) => (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg border ${getSafeColor(food.safety_score)}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <TrendingDown className="w-4 h-4" />
                              <span className="font-semibold">{food.food}</span>
                              <span className="text-xs bg-white px-2 py-0.5 rounded-full">
                                Safety: {food.safety_score}/10
                              </span>
                            </div>
                            <p className="text-sm mb-2">
                              {food.good_day_ratio}% of days after eating this had lower symptoms
                            </p>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span>Avg severity: {food.avg_severity_after}/10</span>
                              <span>•</span>
                              <span>Logged {food.occurrences} times</span>
                              <span>•</span>
                              <span>Confidence: {food.confidence}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Insufficient data message */}
          {results && results.status === 'insufficient_data' && (
            <Card className="bg-white rounded-xl border border-stone-100 p-8 text-center">
              <Info className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="font-nunito font-bold text-[#2D2D2D] mb-2">More Data Needed</h3>
              <p className="text-[#57534E] mb-4">{results.message}</p>
              <p className="text-sm text-[#A8A29E]">
                Keep logging your food and symptoms daily. The more consistent your tracking, 
                the more reliable the analysis will be.
              </p>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default CorrelationAnalyzer;
